import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { customAxiosInstance } from '../axios/axiosInstance';
import { AxiosInstance, AxiosResponse } from 'axios';
import { IError } from '../interfaces/IError';
import { axiosErrorHandler } from '../axios/axiosError';
import { IAsyncProcessRepository } from '../interfaces/IAsyncProcessRepository';
import {
  AsyncProcessApi,
  AsyncProcessStatusItem,
  DeleteAsyncProcessStatusResponse,
  DeleteAsyncProcessStatusResponseInfo,
  ErrorResponse,
  GetAsyncProcessStatusResponse,
  PostAsyncProcessRequest,
  PostAsyncProcessResponse,
  PostAsyncProcessResponseInfo,
} from '../axios/generated/sqsAccessManage/api';

/**
 * 非同期処理のステータス管理を行うリポジトリ。
 * axiosInstanceはpollAsyncProcess内で一度だけ生成して使い回す。
 */
export class AsyncProcessRepository implements IAsyncProcessRepository {
  private cachedAxiosInstance: AxiosInstance | undefined;

  /**
   * axiosInstanceを取得する。初回呼び出し時のみcustomAxiosInstanceを生成し、以降はキャッシュを返す。
   * Cognitoトークン取得+期限検証のオーバーヘッドをポーリング1回目のみに限定する。
   */
  private async getAxiosInstance(): Promise<AxiosInstance> {
    if (!this.cachedAxiosInstance) {
      const instance = await customAxiosInstance('dbAccess');
      if (!instance) {
        throw new Error('認証トークンの取得に失敗しました');
      }
      this.cachedAxiosInstance = instance;
    }
    return this.cachedAxiosInstance;
  }

  /**
   * 非同期処理を起票する
   * @param request - 非同期処理のリクエスト
   */
  async post(request: PostAsyncProcessRequest): Promise<INetworkResult<PostAsyncProcessResponseInfo, IError>> {
    const axiosInstance = await this.getAxiosInstance();
    const asyncProcessApi = new AsyncProcessApi(undefined, undefined, axiosInstance);

    let apiResponse: AxiosResponse<PostAsyncProcessResponse, ErrorResponse>;

    try {
      apiResponse = await asyncProcessApi.postAsyncProcess(request);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 非同期処理のステータスをポーリングで取得する
   * @param actionType - 処理種別でフィルタリング
   */
  async getStatus(actionType?: string): Promise<INetworkResult<AsyncProcessStatusItem[], IError>> {
    const axiosInstance = await this.getAxiosInstance();
    const asyncProcessApi = new AsyncProcessApi(undefined, undefined, axiosInstance);

    let apiResponse: AxiosResponse<GetAsyncProcessStatusResponse, ErrorResponse>;

    try {
      apiResponse = await asyncProcessApi.getAsyncProcessStatus(actionType);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 非同期処理のステータス情報をDynamoDBから削除する
   * @param requestId - 削除対象のリクエストID
   */
  async deleteStatus(requestId: string): Promise<INetworkResult<DeleteAsyncProcessStatusResponseInfo, IError>> {
    const axiosInstance = await this.getAxiosInstance();
    const asyncProcessApi = new AsyncProcessApi(undefined, undefined, axiosInstance);

    let apiResponse: AxiosResponse<DeleteAsyncProcessStatusResponse, ErrorResponse>;

    try {
      apiResponse = await asyncProcessApi.deleteAsyncProcessStatus(requestId);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
